import type { RestockRecommendation } from '@stockwait/shared'
import { useState } from 'react'
import { EmptyState, ErrorState, LoadingState } from '../components/StateViews'
import { api } from '../lib/api'
import { confidenceTone, money, moneyRange, number, percent } from '../lib/format'
import { useApi } from '../lib/useApi'

type Payload = {
  currency: string
  recommendations: RestockRecommendation[]
  methodology: {
    modelVersion: string
    conversionRate: number
    conversionRateSource: string
    conversionRateSampleSize: number
    recencyHalfLifeDays: number
    notes: string[]
  }
}

/**
 * The screen a merchant opens before placing a purchase order (SW-802).
 *
 * The **variant mix leads the design**, because it is the part with no substitute anywhere
 * else. Historical sales data is censored; it records what the merchant had stock of, not
 * what shoppers wanted, and the waitlist is uncensored demand.
 */
export function Recommendations() {
  const [rankBy, setRankBy] = useState<'revenue' | 'margin'>('revenue')
  const { data, error, loading, reload } = useApi<Payload>(
    `/recommendations?rankBy=${rankBy}&limit=25`,
  )
  const [busy, setBusy] = useState<string | null>(null)

  if (loading && !data) return <LoadingState label="Working out what to restock" />
  if (error) return <ErrorState error={error} onRetry={reload} />
  if (!data) return null

  if (data.recommendations.length === 0) {
    return (
      <EmptyState heading="Nothing to recommend yet">
        Recommendations appear once shoppers have joined waitlists. The more demand you capture, the
        sharper the size mix gets.
      </EmptyState>
    )
  }

  /**
   * One cost per product, written to every variant of it.
   *
   * Cost genuinely varies per variant in some catalogues, and the endpoint is per-variant, but
   * for the apparel case this screen exists to serve, every size of a shirt costs the same, and
   * asking for four identical numbers to rank one product is how a feature goes unused.
   *
   * Recomputes immediately afterwards. The nightly job would get there eventually, and a
   * merchant who just typed a number and sees the ranking unchanged concludes it did nothing.
   */
  async function saveCost(rec: RestockRecommendation, value: string) {
    const amount = value.trim()
    if (amount === '') return
    setBusy(rec.productId)
    try {
      const variantIds = rec.variantMix.map((mix) => mix.variantId)
      await api.post('/costs', {
        costs: variantIds.map((variantId) => ({ variantId, cost: amount })),
      })
      await api.post('/demand/recompute')
      shopify.toast.show('Cost saved')
      reload()
    } catch (err) {
      shopify.toast.show(err instanceof Error ? err.message : 'Could not save the cost', {
        isError: true,
      })
    } finally {
      setBusy(null)
    }
  }

  async function feedback(
    productId: string,
    variantId: string,
    action: 'dismissed' | 'snoozed' | 'ordered',
  ) {
    setBusy(variantId)
    try {
      await api.post(`/recommendations/${productId}/feedback`, { action, variantId, days: 30 })
      shopify.toast.show(action === 'ordered' ? 'Marked as ordered' : 'Removed from the list')
      reload()
    } catch {
      shopify.toast.show('Could not save that', { isError: true })
    } finally {
      setBusy(null)
    }
  }

  const { methodology, currency } = data

  // Ranking by margin needs a cost per item, and nothing in the app can record one yet, the
  // endpoint exists, the UI to reach it does not. Offering the option regardless meant a
  // merchant selected it, the order did not change, and nothing said why.
  const anyCostKnown = data.recommendations.some((rec) => rec.marginMinor !== null)

  return (
    <s-stack direction="block" gap="large">
      <s-section>
        <s-stack direction="inline" gap="base" alignItems="center" justifyContent="space-between">
          <s-stack direction="block" gap="small-400">
            <s-heading>Restock plan</s-heading>
            <s-text color="subdued">
              {data.recommendations.length} products with waiting demand, ranked by{' '}
              {rankBy === 'margin' ? 'expected margin' : 'expected revenue'}.
            </s-text>
          </s-stack>
          <s-stack direction="inline" gap="small-300">
            <s-select
              label="Rank by"
              labelAccessibilityVisibility="exclusive"
              value={rankBy}
              onChange={(event: Event) =>
                setRankBy((event.target as HTMLSelectElement).value as 'revenue' | 'margin')
              }
            >
              <s-option value="revenue">Rank by revenue</s-option>
              <s-option value="margin" disabled={!anyCostKnown}>
                {anyCostKnown ? 'Rank by margin' : 'Rank by margin (no cost data)'}
              </s-option>
            </s-select>
            <s-button
              onClick={() =>
                api.download('/export/recommendations.csv', 'stockwait-restock-plan.csv')
              }
            >
              Export CSV
            </s-button>
          </s-stack>
        </s-stack>
      </s-section>

      {data.recommendations.map((rec) => (
        <s-section key={rec.productId}>
          <s-stack direction="block" gap="base">
            <s-stack
              direction="inline"
              gap="base"
              alignItems="start"
              justifyContent="space-between"
            >
              <s-stack direction="inline" gap="small-300" alignItems="center">
                {rec.imageUrl ? <s-thumbnail src={rec.imageUrl} alt="" size="base" /> : null}
                <s-stack direction="block" gap="small-500">
                  <s-text type="strong">
                    {rec.rank}. {rec.productTitle ?? 'Product'}
                  </s-text>
                  <s-stack direction="inline" gap="small-300" alignItems="center">
                    <s-text color="subdued">{number(rec.waitingSubscribers)} waiting</s-text>
                    <s-badge tone={confidenceTone(rec.confidence)}>
                      {rec.confidence} confidence
                    </s-badge>
                  </s-stack>
                </s-stack>
              </s-stack>

              <s-stack direction="block" gap="small-500" alignItems="end">
                {/* A range, never a point estimate. */}
                <s-text type="strong">
                  {moneyRange(rec.demandValueLowMinor, rec.demandValueHighMinor, currency)}
                </s-text>
                {rec.marginMinor !== null ? (
                  <s-text color="subdued">{money(rec.marginMinor, currency)} margin</s-text>
                ) : null}
              </s-stack>
            </s-stack>

            {/*
              The finding. Apparel merchants over-order mediums and under-order the tails
              because their sales data only shows what they had stock of; this is the only
              place the real distribution exists.
            */}
            {rec.variantMix.length > 1 ? (
              <s-box padding="base" background="subdued" borderRadius="base">
                <s-stack direction="block" gap="small-300">
                  <s-text type="strong">Size and variant mix of waiting demand</s-text>
                  {rec.variantMix.map((mix) => (
                    <s-stack
                      key={mix.variantId}
                      direction="inline"
                      gap="small-300"
                      alignItems="center"
                    >
                      <div style={{ minWidth: '5rem' }}>
                        <s-text>{mix.variantTitle ?? '-'}</s-text>
                      </div>
                      <div
                        style={{
                          flex: 1,
                          height: '10px',
                          background: 'var(--s-color-fill-subdued, #e3e3e3)',
                          borderRadius: '5px',
                          overflow: 'hidden',
                        }}
                      >
                        <div
                          style={{
                            width: `${Math.round(mix.share * 100)}%`,
                            height: '100%',
                            background: 'var(--s-color-fill-brand, #303030)',
                          }}
                        />
                      </div>
                      <div style={{ minWidth: '7rem', textAlign: 'right' }}>
                        <s-text color="subdued">
                          {percent(mix.share, 0)} · {number(mix.waitingSubscribers)}
                        </s-text>
                      </div>
                    </s-stack>
                  ))}
                </s-stack>
              </s-box>
            ) : null}

            {rec.recommendedQuantities.length > 0 ? (
              <s-stack direction="block" gap="small-300">
                <s-text type="strong">Suggested reorder</s-text>
                <s-stack direction="inline" gap="small-300">
                  {rec.recommendedQuantities.map((qty) => (
                    <s-chip key={qty.variantId}>
                      {qty.variantTitle ?? '-'}: {qty.recommendedQty}
                    </s-chip>
                  ))}
                </s-stack>
              </s-stack>
            ) : null}

            {/* "Why this ranking?" on every row. A ranking a merchant cannot interrogate is a
                ranking they will not spend money against. */}
            <s-box padding="base" borderRadius="base">
              <s-stack direction="block" gap="small-400">
                <s-text type="strong">Why this ranking?</s-text>
                <s-unordered-list>
                  {rec.reasoning.map((reason) => (
                    <s-list-item key={reason}>
                      <s-text color="subdued">{reason}</s-text>
                    </s-list-item>
                  ))}
                </s-unordered-list>
                <s-text color="subdued">
                  Scaled to total demand: roughly{' '}
                  {number(rec.extrapolatedDemand.estimatedInterestedShoppers)} shoppers may have
                  wanted this, at a {percent(rec.extrapolatedDemand.captureRate, 1)} waitlist
                  capture rate. This is an estimate with wide error bars.
                </s-text>
              </s-stack>
            </s-box>

            <CostField
              rec={rec}
              currency={currency}
              busy={busy === rec.productId}
              onSave={(value) => saveCost(rec, value)}
            />

            <s-stack direction="inline" gap="small-300">
              <s-button
                disabled={busy === rec.variantMix[0]?.variantId}
                onClick={() =>
                  feedback(rec.productId, rec.variantMix[0]?.variantId ?? rec.productId, 'ordered')
                }
              >
                Mark as ordered
              </s-button>
              <s-button
                variant="tertiary"
                onClick={() =>
                  feedback(rec.productId, rec.variantMix[0]?.variantId ?? rec.productId, 'snoozed')
                }
              >
                Snooze 30 days
              </s-button>
              <s-button
                variant="tertiary"
                tone="critical"
                onClick={() =>
                  feedback(
                    rec.productId,
                    rec.variantMix[0]?.variantId ?? rec.productId,
                    'dismissed',
                  )
                }
              >
                Dismiss
              </s-button>
            </s-stack>
          </s-stack>
        </s-section>
      ))}

      {/*
        The methodology travels with the numbers rather than living on a page nobody opens.
        Credibility is the product here, a merchant who cannot see how the figure was built
        is right not to spend against it.
      */}
      <s-section heading="How these numbers are calculated">
        <s-stack direction="block" gap="small-300">
          <s-unordered-list>
            {methodology.notes.map((note) => (
              <s-list-item key={note}>
                <s-text color="subdued">{note}</s-text>
              </s-list-item>
            ))}
          </s-unordered-list>
          <s-text color="subdued">
            Model version {methodology.modelVersion} · conversion rate{' '}
            {percent(methodology.conversionRate)} ({methodology.conversionRateSource}
            {methodology.conversionRateSource === 'shop'
              ? `, ${number(methodology.conversionRateSampleSize)} alerts`
              : ''}
            ) · {methodology.recencyHalfLifeDays}-day recency half-life
          </s-text>
        </s-stack>
      </s-section>
    </s-stack>
  )
}

/**
 * Cost per item for one product.
 *
 * Local state rather than a controlled value from the payload: the merchant is mid-edit, and a
 * reload landing under them would replace what they are typing.
 */
function CostField({
  rec,
  currency,
  busy,
  onSave,
}: {
  rec: RestockRecommendation
  currency: string
  busy: boolean
  onSave: (value: string) => void
}) {
  const [value, setValue] = useState(
    rec.costPerItemMinor === null ? '' : (rec.costPerItemMinor / 100).toFixed(2),
  )

  return (
    <s-grid gridTemplateColumns="1fr auto" gap="base" alignItems="end">
      <s-money-field
        label={
          rec.variantMix.length > 1
            ? `Cost per item (${currency}), applies to all ${rec.variantMix.length} variants`
            : `Cost per item (${currency})`
        }
        value={value}
        min={0}
        onChange={(event: Event) => setValue((event.target as HTMLInputElement).value)}
      />
      <s-button disabled={busy || value.trim() === ''} onClick={() => onSave(value)}>
        {busy ? 'Saving…' : 'Save cost'}
      </s-button>
    </s-grid>
  )
}
